import React, { useEffect, useRef, useState } from "react";

import { useNavigate, useParams } from "react-router";
import { toast } from "react-toastify";
import BackButton from "../../Components/BackButton";
import CustomButton from "../../Components/CustomButton";
import CustomInput from "../../Components/CustomInput";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import { useAddCategoryMutation, useGetCategoryByIdQuery, useUpdateCategoryMutation } from "../../Redux/Apis/Category";
import { useUploadImageMutation } from "../../Redux/Apis/General";
import { objectValidator } from "../../Utils";
import "./style.css";

const SECTIONS = [
  { value: 'pros', label: 'The Pros' },
  { value: 'barn', label: 'The Barn' },
  { value: 'calcutta', label: 'Calcutta' },
  { value: 'greenskeeping', label: 'Greenskeeping' },
];

const EditCategory = () => {

  const navigate = useNavigate()
  const { id } = useParams()

  useEffect(() => {
    document.title = 'Landing List | Edit Category';
  }, []);

  const { data: category } = useGetCategoryByIdQuery({ id })
  const [update, { isLoading, isSuccess, data }] = useUpdateCategoryMutation();
  const [uploadImage, { isLoading: imageLoading }] = useUploadImageMutation();

  const [name, setName] = useState("")
  const [section, setSection] = useState("pros")
  const [subcategories, setSubcategories] = useState([""])
  const [subcatImages, setSubcatImages] = useState([])
  const [currentIcon, setCurrentIcon] = useState("")
  const [categoryImage, setCategoryImage] = useState(null)
  const categoryRef = useRef()

  const buildImageUrl = (path) => {
    let origin = ''
    try { origin = new URL(process.env.REACT_APP_BASE_URL).origin } catch (e) { }
    if (!path) return ''
    if (path.startsWith('http')) return path
    const normalized = path.startsWith('uploads/')
      ? `/${path}`
      : `/uploads/images/${path}`
    return `${origin}${normalized}`
  }

  useEffect(() => {
    if (category && category.data) {
      setName(category?.data?.name)
      setSection(category?.data?.section || "pros")
      setSubcategories(category?.data?.subcategories)
      setCurrentIcon(category?.data?.icon || "")
    }
  }, [category])

  useEffect(() => {
    if (isSuccess) {
      toast.success(data?.message)
      navigate('/categories')
    }
  }, [isSuccess]);

  const onClickSave = async () => {

    let validate = objectValidator({ name, subcategories })

    if (validate) {

      let iconPath = undefined
      if (categoryImage) {
        const formdata = new FormData()
        formdata.append("image", categoryImage)
        await uploadImage(formdata).then(res => {
          iconPath = res?.data?.data?.path
        })
      }

      // Build subcategories payload with optional image uploads
      const subcategoriesPayload = []
      for (let i = 0; i < subcategories.length; i++) {
        const sub = subcategories[i]
        let iconForSub = sub?.icon
        if (subcatImages[i]) {
          const fd = new FormData()
          fd.append('image', subcatImages[i])
          await uploadImage(fd).then(res => {
            iconForSub = res?.data?.data?.path
          })
        }
        subcategoriesPayload.push({
          name: sub?.name || sub,
          ...(iconForSub ? { icon: iconForSub } : {})
        })
      }

      // Build payload and update
      update({
        id,
        payload: {
          name,
          section,
          ...(iconPath ? { icon: iconPath } : {}),
          subcategories: subcategoriesPayload
        }
      })

    }

  }

  const onManipulateSubcategory = (operator, index) => {
    if (operator === "add") {
      let temp = [...subcategories]
      if (temp[temp.length - 1] === "") {
        return toast.error("Please fill the field")
      }
      setSubcategories(e => [...e, { name: "" }])
    } else if (operator === "minus") {
      let temp = [...subcategories]
      temp.splice(index, 1);
      setSubcategories(temp)
    }
  }

  const onChangeSubcategory = (index, value) => {
    let temp = [...subcategories]
    let curr = { ...temp[index] }
    curr.name = value
    temp[index] = curr
    setSubcategories(temp)
  }

  const onChangeSubcategoryImage = (index, file) => {
    const temp = [...subcatImages]
    temp[index] = file
    setSubcatImages(temp)
  }

  return (
    <>
      <DashboardLayout>
        <div className="dashCard mb-4">
          <div className="row mb-3">
            <div className="col-12">
              <h2 className="mainTitle">
                <BackButton />
                Edit Category
              </h2>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-4">
              <CustomInput
                label="Category Name"
                labelClass="mainLabel"
                required
                placeholder="Enter Category Name"
                inputClass="mainInput"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
            <div className="col-4">
              <label className="mainLabel d-block">Section <span className="text-danger">*</span></label>
              <select
                className="sectionSelect"
                value={section}
                onChange={e => setSection(e.target.value)}
              >
                {SECTIONS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="col-4">
              <label className="mainLabel d-block">Category Icon</label>
              {
                (categoryImage || currentIcon) &&
                <div className="d-flex mb-2">
                  <img
                    className="mx-2"
                    src={categoryImage ? URL.createObjectURL(categoryImage) : (() => {
                      // Build absolute image URL from API origin
                      let origin = ''
                      try { origin = new URL(process.env.REACT_APP_BASE_URL).origin } catch (e) { }
                      if (!currentIcon) return ''
                      if (currentIcon.startsWith('http')) return currentIcon
                      // Ensure it lives under /uploads
                      const normalized = currentIcon.startsWith('uploads/')
                        ? `/${currentIcon}`
                        : `/uploads/images/${currentIcon}`
                      return `${origin}${normalized}`
                    })()}
                    style={{ width: '160px', height: '160px', objectFit: 'contain', borderRadius: 12, backgroundColor: '#F0F0F0' }}
                  />
                </div>
              }
              <CustomButton text="Upload" variant="primaryButton" onClick={() => categoryRef.current.click()} />
              <input type="file" hidden ref={categoryRef} onChange={e => setCategoryImage(e.target.files[0])} />
            </div>
          </div>
          <h2 className="mainTitle">
            Subcategories
          </h2>
          {
            subcategories.map((item, index) => (
              <div className="row mb-3">
                <div className="col-6 d-flex flex-row align-items-center">
                  <h5 className="me-3 mb-2">{index + 1}.</h5>
                  <div className="w-100">
                    <CustomInput
                      containerClass="w-100"
                      placeholder="Enter Subcategory"
                      inputClass="mainInput"
                      value={item.name}
                      onChange={e => onChangeSubcategory(index, e.target.value)}
                    />
                    <div className="mt-2">
                      <label className="primaryButton" style={{ cursor: 'pointer', padding: '8px 16px' }}>
                        <span>Upload</span>
                        <input
                          type="file"
                          hidden
                          onChange={e => onChangeSubcategoryImage(index, e.target.files[0])}
                        />
                      </label>
                    </div>
                    {
                      (subcatImages[index] || item?.icon) &&
                      <div className="d-flex mt-2">
                        <img
                          className="mx-2"
                          src={subcatImages[index] ? URL.createObjectURL(subcatImages[index]) : buildImageUrl(item?.icon)}
                          style={{ width: '140px', height: '140px', objectFit: 'contain', borderRadius: 12, backgroundColor: '#F0F0F0' }}
                        />
                      </div>
                    }
                  </div>
                  {
                    subcategories.length > 1 &&
                    <h5 className="text-danger ms-3" onClick={() => onManipulateSubcategory("minus", index)}>-</h5>
                  }
                  {
                    index === subcategories.length - 1 &&
                    <h5
                      className="ms-3"
                      onClick={() => onManipulateSubcategory("add")}>+</h5>
                  }
                </div>
              </div>
            ))
          }
          <div className="row mb-3">
            <div className="col-4">
              <CustomButton
                text="Save"
                onClick={onClickSave}
                loading={isLoading || imageLoading}
              />
            </div>
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default EditCategory;